import {
  boolean,
  index,
  integer,
  numeric,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { productVariants } from "./products";

export const customers = pgTable(
  "customers",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    name: text("name").notNull(),
    phone: text("phone"),
    /** Legacy / derived display string — คงไว้ช่วง transition */
    address: text("address"),
    village: text("village"),
    district: text("district"),
    province: text("province"),
    addressLine: text("address_line"),
    note: text("note"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("customers_by_phone").on(t.phone),
    index("customers_by_province").on(t.province),
    index("customers_by_province_district").on(t.province, t.district),
  ],
);

export const orders = pgTable(
  "orders",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderNumber: text("order_number").notNull().unique(),
    customerId: text("customer_id")
      .notNull()
      .references(() => customers.id, { onDelete: "restrict" }),
    status: text("status").notNull().default("draft"),
    deliveryMethod: text("delivery_method").notNull().default("carrier"),
    shippingCarrier: text("shipping_carrier"),
    shippingBranch: text("shipping_branch"),
    paymentType: text("payment_type").notNull().default("prepaid"),
    shippingFee: numeric("shipping_fee", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    discountAmount: numeric("discount_amount", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    subtotal: numeric("subtotal", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    total: numeric("total", { precision: 12, scale: 2 })
      .notNull()
      .default("0"),
    note: text("note"),
    paidAt: timestamp("paid_at", { withTimezone: true, mode: "date" }),
    packedAt: timestamp("packed_at", { withTimezone: true, mode: "date" }),
    shippedAt: timestamp("shipped_at", { withTimezone: true, mode: "date" }),
    deliveredAt: timestamp("delivered_at", {
      withTimezone: true,
      mode: "date",
    }),
    pickedUpAt: timestamp("picked_up_at", { withTimezone: true, mode: "date" }),
    trackingNumber: text("tracking_number"),
    returnedAt: timestamp("returned_at", { withTimezone: true, mode: "date" }),
    returnNote: text("return_note"),
    /** ค่าขนส่งตีกลับที่ร้านจ่าย */
    returnShippingCost: numeric("return_shipping_cost", {
      precision: 12,
      scale: 2,
    }),
    /** ค่าขนส่งขาไปที่ร้านจ่ายขนส่ง (optional) */
    outboundCarrierCost: numeric("outbound_carrier_cost", {
      precision: 12,
      scale: 2,
    }),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("orders_by_customer").on(t.customerId),
    index("orders_by_status").on(t.status),
    index("orders_by_created").on(t.createdAt),
  ],
);

export const orderItems = pgTable(
  "order_items",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderId: text("order_id")
      .notNull()
      .references(() => orders.id, { onDelete: "cascade" }),
    variantId: text("variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    productName: text("product_name").notNull(),
    variantName: text("variant_name").notNull(),
    quantity: integer("quantity").notNull(),
    unitPrice: numeric("unit_price", { precision: 12, scale: 2 }).notNull(),
    lineTotal: numeric("line_total", { precision: 12, scale: 2 }).notNull(),
    isBonus: boolean("is_bonus").notNull().default(false),
    /** simple = บรรทัดปกติ / catalog bundle, ad_hoc_kit = ชุดครั้งเดียว (Phase 15) */
    lineType: text("line_type").notNull().default("simple"),
    /** ชื่อชุดที่ผู้ใช้ใส่ — ใช้เมื่อ lineType = ad_hoc_kit */
    kitName: text("kit_name"),
  },
  (t) => [
    index("order_items_by_order").on(t.orderId),
    index("order_items_by_variant").on(t.variantId),
  ],
);

/** Snapshot ส่วนประกอบของชุด ad-hoc บน order line (Phase 15) */
export const orderItemKitComponents = pgTable(
  "order_item_kit_components",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderItemId: text("order_item_id")
      .notNull()
      .references(() => orderItems.id, { onDelete: "cascade" }),
    componentVariantId: text("component_variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    quantity: integer("quantity").notNull(),
    productName: text("product_name").notNull(),
    variantName: text("variant_name").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
  },
  (t) => [
    uniqueIndex("order_item_kit_components_unique").on(
      t.orderItemId,
      t.componentVariantId,
    ),
    index("order_item_kit_components_by_item").on(t.orderItemId),
  ],
);

export type OrderLineType = "simple" | "ad_hoc_kit";

export type OrderStatus =
  | "draft"
  | "paid"
  | "ready_to_ship"
  | "shipped"
  | "delivered"
  | "picked_up"
  | "returned"
  | "cancelled";

/** ຊຳລະກ່ອນ (โอนก่อนส่ง) | COD (เก็บเงินปลายทาง) */
export type PaymentType = "prepaid" | "cod";

/** ວິທີຮັບສິນຄ້າ */
export type DeliveryMethod = "carrier" | "pickup" | "self_delivery";
